import { uploadImageToImageKit } from './imagekitService';
import { uploadImageToImgBB } from './imgbbService';
import { getImageStorageSettings } from './imageStorageService';
// import { convertToBase64 } from '../utils/imageUtils'; // تم إزالة التخزين المحلي
import { ProductImage } from '../utils/types';

/**
 * واجهة لنتيجة تحميل الصورة
 */
export interface ProductImageResult {
  imageUrl: string;       // رابط الصورة الرئيسي للعرض
  imgbbUrl?: string;      // رابط ImgBB (إذا تم استخدامه)
  imagekitUrl?: string;   // رابط ImageKit.io (إذا تم استخدامه)
  localUrl?: string;      // رابط محلي (إذا تم استخدامه)
}

/**
 * تحميل صورة منتج وإرجاع جميع الروابط المطلوبة
 * @param file ملف الصورة
 * @param folder المجلد (اختياري)
 * @returns وعد بكائن يحتوي على جميع روابط الصورة
 */
export const uploadProductImage = async (file: File, folder?: string): Promise<ProductImageResult> => {
  try {
    console.log('بدء تحميل صورة المنتج...', file.name, file.size);

    // استخدام الخدمة الموحدة
    const { uploadUnifiedImage } = await import('./unifiedImageService');
    const uploadResult = await uploadUnifiedImage({
      file: file,
      context: 'inventory',
      folder: folder || 'inventory'
    });

    console.log('تم تحميل الصورة بنجاح:', uploadResult);

    const result: ProductImageResult = {
      imageUrl: uploadResult.url,
      imgbbUrl: uploadResult.imgbbUrl,
      imagekitUrl: uploadResult.imagekitUrl
    };

    console.log('اكتمل تحميل صورة المنتج بنجاح:', result);
    return result;
  } catch (error) {
    console.error('خطأ في تحميل صورة المنتج:', error);
    throw error; // إعادة رمي الخطأ
  }
};

/**
 * تحديث كائن المنتج بروابط الصورة
 * @param product كائن المنتج
 * @param imageResult نتيجة تحميل الصورة
 * @returns كائن المنتج المحدث
 */
export const updateProductWithImage = (product: any, imageResult: ProductImageResult): any => {
  return {
    ...product,
    imageUrl: imageResult.imageUrl,
    imgbbUrl: imageResult.imgbbUrl,
    imagekitUrl: imageResult.imagekitUrl,
    image: imageResult.localUrl || imageResult.imageUrl // للتوافق مع الكود القديم
  };
};

/**
 * تحميل مجموعة من الصور للمنتج
 * @param files مصفوفة ملفات الصور
 * @param folder المجلد (اختياري)
 * @returns وعد بمصفوفة نتائج تحميل الصور
 */
export const uploadMultipleProductImages = async (files: File[], folder?: string): Promise<ProductImageResult[]> => {
  try {
    console.log(`بدء تحميل ${files.length} صور للمنتج...`);

    // التحقق من وجود ملفات
    if (!files || files.length === 0) {
      console.log('لا توجد ملفات لتحميلها');
      return [];
    }

    // توقف قصير بين كل تحميل لمنع التحميل المتزامن الذي قد يسبب مشاكل
    const uploadOneByOne = async () => {
      const results: ProductImageResult[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`تحميل الصورة ${i + 1}/${files.length}: ${file.name}`);
        
        try {
          const result = await uploadProductImage(file, folder);
          if (result && result.imageUrl) {
            results.push(result);
            console.log(`تم تحميل الصورة ${i + 1} بنجاح:`, result);
          } else {
            console.error(`فشل تحميل الصورة ${i + 1}: لا يوجد رابط صورة`);
          }
        } catch (error) {
          console.error(`فشل تحميل الصورة ${i + 1}:`, error);
        }
        
        // انتظار 500 مللي ثانية بين كل تحميل لمنع التحميل المتزامن
        if (i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      return results;
    };
    
    // استخدام طريقة التحميل المتسلسل (one by one)
    const successfulUploads = await uploadOneByOne();

    // تسجيل نتائج التحميل
    console.log(`تم تحميل ${successfulUploads.length} من أصل ${files.length} صور بنجاح`);

    // للتشخيص: عرض الروابط لكل صورة
    successfulUploads.forEach((result, index) => {
      console.log(`صورة ${index + 1}:`, {
        imageUrl: result.imageUrl,
        imgbbUrl: result.imgbbUrl,
        imagekitUrl: result.imagekitUrl
      });
    });

    return successfulUploads;
  } catch (error) {
    console.error('خطأ في تحميل مجموعة الصور:', error);
    throw error;
  }
};

/**
 * تحويل كائنات الصور إلى تنسيق صالح للتخزين في Firestore
 * @param images مصفوفة كائنات الصور
 * @returns مصفوفة كائنات الصور المحولة
 */
export const prepareImagesForFirestore = (images: ProductImage[]): ProductImage[] => {
  console.log('تحويل كائنات الصور للتخزين في Firestore:', images);

  if (!images || !Array.isArray(images) || images.length === 0) {
    console.log('لا توجد صور للتحويل، إرجاع مصفوفة فارغة');
    return [];
  }

  // نسخ المصفوفة لتجنب تعديل الكائن الأصلي
  let imagesCopy = [...images];

  // التأكد من وجود صورة رئيسية واحدة على الأقل
  let hasMainImage = imagesCopy.some(img => img.isMain === true);
  if (!hasMainImage && imagesCopy.length > 0) {
    console.log('لا توجد صورة رئيسية، تعيين الصورة الأولى كصورة رئيسية');
    imagesCopy[0] = { ...imagesCopy[0], isMain: true };
  }

  const preparedImages = imagesCopy.map((image, index) => {
    // التأكد من أن كل حقل له قيمة صالحة
    const preparedImage: ProductImage = {
      id: image.id || `img_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      url: image.url || '',
      imgbbUrl: image.imgbbUrl || '',
      imagekitUrl: image.imagekitUrl || '',
      isMain: image.isMain === true ? true : false,
      sortOrder: typeof image.sortOrder === 'number' ? image.sortOrder : index,
      caption: image.caption || '',
      createdAt: image.createdAt || new Date().toISOString()
    };

    return preparedImage;
  });

  console.log('كائنات الصور بعد التحويل:', preparedImages);
  // تسجيل البيانات للتأكد من عدم وجود قيم undefined
  console.log('كائنات الصور بعد التحويل (JSON):', JSON.stringify(preparedImages));
  return preparedImages;
};

export default {
  uploadProductImage,
  uploadMultipleProductImages,
  updateProductWithImage,
  prepareImagesForFirestore
};
